import { Injectable } from '@angular/core';
import { TranslocoService, isEmpty } from '@jsverse/transloco';
import { switchMap, tap, take } from 'rxjs/operators';
import { Observable, iif, of } from 'rxjs';

import { RangeTypePipe } from '@shared/filters/pipes/range-type-format.pipe';
import { PriceAbbreviationPipe } from '@app/pipes/price-abbreviation.pipe';
import { StopNamePipe } from '@app/pipes/stop-name.pipe';
import {
  CheckboxFilter, CheckboxFilterVm, PickedRangeValues, RangeType
} from '@shared/filters/entities/fetch-filters.response';
import { FilterApplier, FilterTypes, SectorTypes } from '@shared/flight-itinerary/entities/filters';
import { FILTER_TITLES } from '@flight-results/constants/filters.constants';
import { TripTypes } from '@app/entities/trip-types';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { SECONDS_IN_HOURS, secondsToStringTime } from '@flight-results/mappers/filters.mapper';
import { isNullOrUndefined } from '@app/util/util';
import { GlobalFacade } from '@app/global.facade';
import { AirFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { DIRECT_STOP_VALUE } from '@shared/magic-filter-bar/constants/filters.contants';
import { getDestinationByV2 } from '@packages/packages-search-bar/mappers/packages-url.mapper';

@Injectable({
  providedIn: 'root'
})
export class AirFilterBarService {

  constructor(
    private readonly translateService: TranslocoService,
    private readonly priceAmountPipe: PriceAbbreviationPipe,
    private readonly rangeTypePipe: RangeTypePipe,
    private readonly stopName: StopNamePipe,
    private readonly amplitude: PropertiesAmplitudeService,
    private readonly triggerAmplitudeService: TriggerAmplitudeService,
    private readonly globalFacade: GlobalFacade,
  ) { }

  get sortingHasBeenChangedByAir$(): Observable<boolean> {
    return this.globalFacade.ui.results.filters.sortingHasBeenChanged$;
  }

  get airFilterVm$(): Observable<AirFilterVm[]> {
    return this.globalFacade.airFilterVm$();
  }

  public sortingHasBeenChangedByAir(payload: boolean): void {
    this.globalFacade.ui.results.filters.sortingHasBeenChanged(payload);
  }

  public isActiveCheckboxOption(pickedCheckboxes: string[], item: CheckboxFilterVm): boolean {
    return pickedCheckboxes.includes(item.Text);
  }

  public getCheckboxLabel(item: CheckboxFilter, type: FilterTypes): string {
    if (type === FilterTypes.Stops) {
      return this.translateService.translate(this.stopName.transform(item.Text));
    } else {
      return item.TextTransformed ? item.TextTransformed : item.Text;
    }
  }

  public activeCheckboxOptions(pickedCheckboxes: unknown[], values: CheckboxFilterVm[]): CheckboxFilterVm[] {
    return values.filter(value => value.Text !== DIRECT_STOP_VALUE && pickedCheckboxes.includes(value.Text));
  }

  public rangeType(type: string): string {
    switch (type) {
      case FilterTypes.PriceRange:
        return RangeType.PRICE;
      case FilterTypes.DepartureTimes:
        return RangeType.NUMBER_TIME;
      case FilterTypes.SectorDurations:
        return RangeType.TIME;
      default:
        return null;
    }
  }

  public getItemLabel(label: string, isTitle: boolean | undefined = false): string {
    switch (label) {
      case FilterTypes.Airlines:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.AEROLINE';
      case FilterTypes.SectorDurations:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.FLIGHT_DURATION';
      case FilterTypes.DepartureAirports:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.FLIGHT_DEPARTURE_AIRPORT';
      case FilterTypes.ArrivalAirports:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.FLIGHT_ARRIVAL_AIRPORT';
      case FilterTypes.Tags:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.TAGS';
      case FilterTypes.AirFilterTabs:
        return 'HOTELS_MODULE.RESULT_HOTELS.SORTER_LABEL';
      case FilterTypes.DepartureTimes:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.FLIGHT_DEPARTURE';
      case FilterTypes.PriceRange:
        return isTitle ?
          'FLIGHT_RESULTS_MODULE.FILTER_SECTION.PRICE_CURRENCY' :
          'FLIGHT_RESULTS_MODULE.FILTER_SECTION.PRICE';
      default:
        return FILTER_TITLES[label] ?? '';
    }
  }

  public getLabelWithValuesToFilterBarTag(
    filter: AirFilterVm,
    pickedCheckboxes: string[],
    isCheckboxFilter: boolean,
    isActiveCheckboxOption: boolean,
    isLastSector: boolean,
    codePlaces: { departure: string, arrival: string },
    rangeValue?: { range: PickedRangeValues, key: number }
  ): string {
    if (isCheckboxFilter) {
      return this.checkboxLabel(filter, pickedCheckboxes, isActiveCheckboxOption);
    } else if (filter.type === FilterTypes.PriceRange && !isEmpty(rangeValue?.range)) {
      return rangeValue?.range[1] ?
        `${this.priceAmountPipe.transform(
          rangeValue?.range[0], true)} - ${this.priceAmountPipe.transform(rangeValue?.range[1], true)}` :
        this.priceAmountPipe.transform(rangeValue?.range[0], true);
    } else if (
      (filter.type === FilterTypes.DepartureTimes || filter.type === FilterTypes.SectorDurations) &&
      !isEmpty(rangeValue?.range)
    ) {
      return `${this.getCriteriaByFilterBar(
        filter.type, +rangeValue?.key, isLastSector, codePlaces)}: ${this.rangeTypePipe.transform(
        rangeValue?.range[0], this.rangeType(filter.type), true)} - ${this.rangeTypePipe.transform(
        rangeValue?.range[1], this.rangeType(filter.type), true)}`;
    } else {
      return this.getItemLabel(filter.type);
    }
  }

  public getCriteriaByLabel(
    departure: string, arrival: string, type: FilterTypes, text?: string
  ): string {
    if (isNullOrUndefined(text)) {
      return '';
    }
    switch (type) {
      case FilterTypes.DepartureTimes:
      case FilterTypes.SectorDurations:
        const tripType: TripTypes = parseInt(text);
        return tripType === TripTypes.DEPARTURE ? `${departure} - ${arrival}` : `${arrival} - ${departure}`;
      case FilterTypes.ArrivalAirports:
      case FilterTypes.DepartureAirports:
        return text === SectorTypes.Sector2 ? `${arrival} - ${departure}` : `${departure} - ${arrival}`;
      case FilterTypes.AirFilterTabs:
        return text === SectorTypes.Sector2 ? arrival : departure;
      default:
        return '';
    }
  }

  public trackSelectFilterAmplitudeEvent(searchBy: SearchBy, filter: FilterApplier): void {
    this.amplitude.searchProperties$(searchBy).pipe(
      take(1),
      switchMap(searchProps => iif(
        () => (filter.type === FilterTypes.DepartureTimes || filter.type === FilterTypes.SectorDurations),
        of({
          searchProps,
          eventProps: {
            FILTERGROUP: filter.type,
            FILTERVALUE: [...filter.value as string[]].map(value => {
              const transformValue = filter.type === FilterTypes.DepartureTimes ? SECONDS_IN_HOURS : 1;
              return secondsToStringTime(parseFloat(value) * transformValue);
            })
          }
        }),
        of({ searchProps, eventProps: { FILTERGROUP: filter.type, FILTERVALUE: filter.value } })
      )),
      switchMap(({ eventProps, searchProps }) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
        AMPLITUDE_EVENTS.SELECT_FILTER, eventProps, searchProps
      ).pipe(
        take(1),
        tap(trigger => triggerAmplitudeEventApp(trigger))
      ))
    ).subscribe().unsubscribe();
  }

  private getCriteriaByFilterBar(
    type: FilterTypes,
    sector: number,
    isLastSector: boolean,
    codePlaces: { departure: string, arrival: string },
  ): string {
    const { arrival, departure } = codePlaces;
    const destinationByV2 = getDestinationByV2(arrival);
    const destination = destinationByV2.Code;
    const departureToArrivalText = `${departure} > ${destination}`;
    const arrivalToDepartureText = `${destination} > ${departure}`;
    switch (type) {
      case FilterTypes.SectorDurations:
      case FilterTypes.DepartureTimes:
        const filterLabel = type === FilterTypes.SectorDurations ? 'FLIGHT_DURATION_SHORT' : 'FLIGHT_DEPARTURE_SHORT';
        const criteria = (sector === 0 || sector === TripTypes.DEPARTURE) && !isLastSector ?
          departureToArrivalText :
          arrivalToDepartureText;
        return this.translateService.translate(
          `FLIGHT_RESULTS_MODULE.FILTER_SECTION.${filterLabel}`,
          { filterCriteria: criteria });
      default:
        return arrivalToDepartureText;
    }
  }

  private checkboxLabel(filter: AirFilterVm, pickedCheckboxes: string[], isActiveCheckboxOption: boolean): string {
    if (isActiveCheckboxOption) {
      return this.activeCheckboxOptions(pickedCheckboxes, filter.value as CheckboxFilterVm[])
        .filter(item => this.isActiveCheckboxOption(pickedCheckboxes, item))
        .map(item => this.getCheckboxLabel(item, filter.type))
        .join(', ');
    } else {
      return this.getItemLabel(filter.type);
    }
  }

}
